import { useState, useEffect, useMemo, useRef } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { Html5QrcodeScanner } from "html5-qrcode";
import { openDB } from 'idb';
import html2canvas from 'html2canvas';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// CONFIG
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const APP_NAME    = "BLOC TRACKER";
const APP_TAGLINE = "Climb Log Repeat";

const HOLD_TYPES = [
  { label:"Crimps" },
  { label:"Slopers" },
  { label:"Overhang" },
  { label:"Pinches" },
  { label:"Dynos" },
  { label:"Compression" },
  { label:"Mantles" },
  { label:"Sidepulls" },
  { label:"Other" },
];

const SENT_PROMPT    = "What worked? (e.g. left hand deadpoint, hip flag)";
const PROJECT_PROMPT = "What held you back? (e.g. foot slipped on heel hook)";

const GRADES    = ["VB","V0","V1","V2","V3","V4","V5","V6","V7","V8","V9","V10","V11","V12"];
const GRADE_NUM = Object.fromEntries(GRADES.map((g,i) => [g,i]));

const COLOUR_HEX = {
  White:"#f5f5f0", Yellow:"#fde047", Orange:"#fb923c", Green:"#4ade80",
  Blue:"#60a5fa",  Red:"#f87171",   Pink:"#f472b6",   Purple:"#a78bfa",
  Black:"#1f2937", Brown:"#92400e", Grey:"#9ca3af",   Teal:"#2dd4bf",
};

const INITIAL_GYMS = [
  { id:"g1", name:"The Depot", colours:[
    { name:"Yellow", hex:"#fde047", grade:"V2" },
    { name:"Green",  hex:"#4ade80", grade:"V4" },
    { name:"Blue",   hex:"#60a5fa", grade:"V5" },
    { name:"Red",    hex:"#f87171", grade:"V6" },
    { name:"Black",  hex:"#1f2937", grade:"V8" },
  ]},
  { id:"g2", name:"The Climbing Works", colours:[
    { name:"White",  hex:"#f5f5f0", grade:"V3" },
    { name:"Orange", hex:"#fb923c", grade:"V5" },
    { name:"Purple", hex:"#a78bfa", grade:"V6" },
    { name:"Red",    hex:"#f87171", grade:"V7" },
  ]},
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// THEME
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const T = {
  bg:"#0d0f14", surface:"#161a24", card:"#1c2130", border:"#252d40",
  accent:"#e8ff47", text:"#f0f4ff", muted:"#8892a4",
  green:"#4ade80", red:"#f87171", blue:"#60a5fa",
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// INDEXEDDB SETUP
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
let dbPromise;

const getDB = () => {
  if (!dbPromise) {
    dbPromise = openDB('bloc-tracker-videos', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('videos')) {
          db.createObjectStore('videos');
        }
      },
    });
  }
  return dbPromise;
};

const saveVideo = async (videoId, file) => {
  try {
    const db = await getDB();
    await db.put('videos', file, videoId);
  } catch (err) {
    console.error('Failed to save video:', err);
  }
};

const getVideo = async (videoId) => {
  try {
    const db = await getDB();
    return await db.get('videos', videoId);
  } catch (err) {
    console.error('Failed to get video:', err);
    return null;
  }
};

const deleteVideo = async (videoId) => {
  try {
    const db = await getDB();
    await db.delete('videos', videoId);
  } catch (err) {
    console.error('Failed to delete video:', err);
  }
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// UTILS
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function isLight(hex) {
  const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16);
  return (r*299+g*587+b*114)/1000 > 155;
}
function load(key, fallback) {
  try { const v=localStorage.getItem(key); return v?JSON.parse(v):fallback; } catch { return fallback; }
}
function save(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}
function getNow() {
  return new Date().toISOString().slice(0, 16);
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// IMAGE CARD GENERATOR
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const generateShareCard = async (problem) => {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.width = '1080px';
  container.style.height = '1920px';
  document.body.appendChild(container);

  container.innerHTML = `
    <div style="
      width: 1080px;
      height: 1920px;
      background: linear-gradient(135deg, #0d0f14 0%, #1c2130 100%);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 60px;
      box-sizing: border-box;
      font-family: system-ui, -apple-system, sans-serif;
    ">
      <div style="
        background: #1c2130;
        border: 2px solid #252d40;
        border-radius: 30px;
        padding: 80px;
        width: 100%;
        text-align: center;
      ">
        <div style="
          color: ${problem.sent ? '#4ade80' : '#f87171'};
          font-size: 100px;
          font-weight: 900;
          margin-bottom: 40px;
        ">
          ${problem.sent ? 'SENT' : 'PROJECT'}
        </div>
        <div style="
          color: #f0f4ff;
          font-size: 72px;
          font-weight: 800;
          margin-bottom: 30px;
        ">
          ${problem.type === 'kilter' ? problem.climbName : problem.colour}
        </div>
        <div style="
          color: #e8ff47;
          font-size: 120px;
          font-weight: 900;
          margin-bottom: 40px;
        ">
          ${problem.grade}
        </div>
        ${problem.type === 'kilter' ? `
          <div style="color: #60a5fa; font-size: 48px; font-weight: 700;">
            ${problem.angle} Kilter Board
          </div>
        ` : `
          <div style="color: #8892a4; font-size: 48px; font-weight: 700;">
            ${problem.holdType || ''}
          </div>
        `}
      </div>
      <div style="
        color: #8892a4;
        font-size: 42px;
        font-weight: 700;
        margin-top: 60px;
      ">
        BLOC TRACKER
      </div>
    </div>
  `;

  const canvas = await html2canvas(container, {
    width: 1080,
    height: 1920,
    scale: 1,
    backgroundColor: '#0d0f14',
  });

  document.body.removeChild(container);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.95);
  });
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// QR SCANNER COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function QRScanner({ onScan }) {
  useEffect(() => {
    const scanner = new Html5QrcodeScanner(
      "qr-reader",
      { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 },
      false
    );
    scanner.render(
      (decodedText) => { onScan(decodedText); scanner.clear(); },
      (error) => {}
    );
    return () => { scanner.clear().catch(console.error); };
  }, [onScan]);

  return <div id="qr-reader" style={{ width: "100%" }} />;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// VIDEO PLAYER COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function VideoPlayer({ videoId, style }) {
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!videoId) { setLoading(false); return; }
    const loadVideo = async () => {
      const file = await getVideo(videoId);
      if (file) setVideoUrl(URL.createObjectURL(file));
      setLoading(false);
    };
    loadVideo();
    return () => { if (videoUrl) URL.revokeObjectURL(videoUrl); };
  }, [videoId]);

  if (!videoId) return null;
  if (loading) return <div style={{ color: T.muted, fontSize: 12 }}>Loading video...</div>;
  if (!videoUrl) return <div style={{ color: T.red, fontSize: 12 }}>Video not found</div>;

  return <video src={videoUrl} controls style={style} preload="metadata" />;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SHARE BUTTON COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function ShareButton({ problem, videoId, style }) {
  const [sharing, setSharing] = useState(false);

  const handleShare = async () => {
    setSharing(true);
    try {
      const text = `${problem.sent ? 'Sent' : 'Tried'} ${
        problem.type === 'kilter' ? problem.climbName : problem.colour
      } ${problem.grade}${
        problem.type === 'kilter' ? ` - ${problem.angle} Kilter` : ''
      }`;

      if (videoId) {
        const file = await getVideo(videoId);
        if (file) {
          const videoFile = new File([file], 'climb.mp4', { type: 'video/mp4' });
          if (navigator.share && navigator.canShare({ files: [videoFile] })) {
            await navigator.share({ files: [videoFile], text });
          } else {
            const url = URL.createObjectURL(file);
            const a = document.createElement('a');
            a.href = url; a.download = `climb-${problem.grade}.mp4`; a.click();
            URL.revokeObjectURL(url);
            alert('Video downloaded! Upload to Instagram manually.');
          }
        }
      } else {
        const imageBlob = await generateShareCard(problem);
        const imageFile = new File([imageBlob], 'climb.jpg', { type: 'image/jpeg' });
        if (navigator.share && navigator.canShare({ files: [imageFile] })) {
          await navigator.share({ files: [imageFile], text });
        } else {
          const url = URL.createObjectURL(imageBlob);
          const a = document.createElement('a');
          a.href = url; a.download = 'climb.jpg'; a.click();
          URL.revokeObjectURL(url);
          alert('Image downloaded! Upload to Instagram manually.');
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') alert('Share failed. Try again.');
    } finally {
      setSharing(false);
    }
  };

  return (
    <button onClick={handleShare} disabled={sharing} style={{
      background: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
      color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px",
      fontSize: 12, fontWeight: 700, cursor: sharing ? "not-allowed" : "pointer",
      opacity: sharing ? 0.6 : 1, ...style,
    }}>
      {sharing ? "Sharing..." : videoId ? "Share Video" : "Share"}
    </button>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SMALL COMPONENTS
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function ColourPill({ colour, hex, grade, selected }) {
  return (
    <div style={{
      background:hex, color:isLight(hex)?"#111":"#fff", borderRadius:10,
      padding:"10px 8px", fontWeight:800, fontSize:13, textAlign:"center",
      border:selected?"3px solid #fff":"2px solid transparent",
      boxShadow:selected?`0 0 0 3px ${hex}88`:"none", lineHeight:1.3,
    }}>
      <div>{colour}</div>
      <div style={{ fontSize:11, opacity:0.8, marginTop:3 }}>{grade}</div>
    </div>
  );
}

function Crumbs({ colour, hold, onBackColour, onBackHold }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16, flexWrap:"wrap" }}>
      {colour && (
        <div style={{ background:colour.hex, color:isLight(colour.hex)?"#111":"#fff", borderRadius:8, padding:"6px 12px", fontWeight:800, fontSize:13 }}>
          {colour.name} / {colour.grade}
        </div>
      )}
      {hold && (
        <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, padding:"6px 12px", fontWeight:700, fontSize:13, color:T.text }}>
          {hold}
        </div>
      )}
      <button onClick={onBackHold||onBackColour} style={{ background:"none", border:`1px solid ${T.border}`, color:T.muted, borderRadius:8, padding:"6px 12px", fontSize:12, fontWeight:700, cursor:"pointer" }}>Back</button>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// EDIT PROBLEM MODAL
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function EditProblemModal({ problem, onSave, onClose, onDelete }) {
  const [sent, setSent] = useState(problem.sent);
  const [note, setNote] = useState(problem.note || "");
  const [grade, setGrade] = useState(problem.grade);

  const handleSave = () => {
    onSave({ ...problem, sent, note, grade });
    onClose();
  };

  const handleDelete = () => {
    if (window.confirm("Delete this climb?")) {
      onDelete(problem.id);
      onClose();
    }
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"#000c", display:"flex", alignItems:"center", justifyContent:"center", zIndex:100, padding:16 }}>
      <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:16, padding:20, width:"100%", maxWidth:400 }}>
        <div style={{ fontWeight:800, fontSize:18, marginBottom:16 }}>Edit Climb</div>
        
        {/* Climb info */}
        <div style={{ background:T.card, borderRadius:10, padding:12, marginBottom:16 }}>
          <div style={{ fontWeight:700, fontSize:14 }}>
            {problem.type === "kilter" ? problem.climbName : problem.colour}
          </div>
          {problem.type === "kilter" && (
            <div style={{ color:T.muted, fontSize:12 }}>{problem.angle}</div>
          )}
        </div>

        {/* Grade */}
        <div style={{ marginBottom:16 }}>
          <label style={{ color:T.muted, fontSize:11, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:6, display:"block" }}>Grade</label>
          <select value={grade} onChange={e => setGrade(e.target.value)} style={{ background:T.card, border:`1px solid ${T.border}`, color:T.text, borderRadius:10, padding:"12px 14px", fontSize:15, width:"100%", outline:"none" }}>
            {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>

        {/* Sent / Project toggle */}
        <div style={{ marginBottom:16 }}>
          <label style={{ color:T.muted, fontSize:11, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:6, display:"block" }}>Result</label>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <button onClick={() => setSent(true)} style={{
              background: sent ? "#4ade8033" : T.card,
              border: sent ? "2px solid #4ade80" : `1px solid ${T.border}`,
              color: sent ? "#4ade80" : T.muted,
              borderRadius:10, padding:12, fontWeight:700, fontSize:14, cursor:"pointer",
            }}>SENT</button>
            <button onClick={() => setSent(false)} style={{
              background: !sent ? "#f8717133" : T.card,
              border: !sent ? "2px solid #f87171" : `1px solid ${T.border}`,
              color: !sent ? "#f87171" : T.muted,
              borderRadius:10, padding:12, fontWeight:700, fontSize:14, cursor:"pointer",
            }}>PROJECT</button>
          </div>
        </div>

        {/* Note */}
        <div style={{ marginBottom:16 }}>
          <label style={{ color:T.muted, fontSize:11, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:6, display:"block" }}>Notes</label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={3}
            style={{ background:T.card, border:`1px solid ${T.border}`, color:T.text, borderRadius:10, padding:"12px 14px", fontSize:15, width:"100%", outline:"none", resize:"none", fontFamily:"inherit" }}
          />
        </div>

        {/* Buttons */}
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={handleDelete} style={{
            background:"none", color:T.red, border:`1px solid ${T.red}44`,
            borderRadius:10, padding:12, fontWeight:700, fontSize:14, cursor:"pointer",
          }}>Delete</button>
          <button onClick={onClose} style={{
            flex:1, background:T.card, color:T.muted, border:`1px solid ${T.border}`,
            borderRadius:10, padding:12, fontWeight:700, fontSize:14, cursor:"pointer",
          }}>Cancel</button>
          <button onClick={handleSave} style={{
            flex:1, background:T.accent, color:"#000", border:"none",
            borderRadius:10, padding:12, fontWeight:800, fontSize:14, cursor:"pointer",
          }}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN APP
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export default function App() {
  const [gyms,     setGyms]     = useState(() => load("bt_gyms",     INITIAL_GYMS));
  const [sessions, setSessions] = useState(() => load("bt_sessions", []));
  const [screen,   setScreen]   = useState("log");
  const [expanded, setExpanded] = useState(null);

  const [activeGymId, setActiveGymId] = useState(() => load("bt_gyms", INITIAL_GYMS)[0]?.id || "");
  const [staging,     setStaging]     = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sessionDateTime, setSessionDateTime] = useState(getNow);

  // Edit modal
  const [editingProblem, setEditingProblem] = useState(null);
  const [editingSessionId, setEditingSessionId] = useState(null);

  // Logging flow
  const [step,         setStep]         = useState(1);
  const [pickedColour, setPickedColour] = useState(null);
  const [pickedHold,   setPickedHold]   = useState(null);
  const [customHold,   setCustomHold]   = useState("");
  const [showCustom,   setShowCustom]   = useState(false);
  const [pendingSent,  setPendingSent]  = useState(null);
  const [note,         setNote]         = useState("");
  const [videoFile,    setVideoFile]    = useState(null);

  // Kilter integration
  const [showScanner,       setShowScanner]       = useState(false);
  const [showProfileScanner, setShowProfileScanner] = useState(false);
  const [fetchedClimb,      setFetchedClimb]      = useState(null);
  const [kilterInput,       setKilterInput]       = useState("");
  const [loading,           setLoading]           = useState(false);
  const [kilterImported,    setKilterImported]    = useState(() => load("kilterImported", false));
  const [kilterHistoryCount, setKilterHistoryCount] = useState(() => load("kilterHistoryCount", 0));
  const [kilterUserId,      setKilterUserId]      = useState(() => load("kilterUserId", null));

  useEffect(() => { save("bt_gyms", gyms); }, [gyms]);
  useEffect(() => { save("bt_sessions", sessions); }, [sessions]);

  const gymMap    = useMemo(() => Object.fromEntries(gyms.map(g=>[g.id,g])), [gyms]);
  const activeGym = gymMap[activeGymId];
  const gymColours = activeGym?.colours || [];
  const isKilterMode = activeGymId === "kilter";

  const resetFlow = () => {
    setStep(1); setPickedColour(null); setPickedHold(null);
    setCustomHold(""); setShowCustom(false); setPendingSent(null); setNote("");
    setVideoFile(null); setShowScanner(false); setFetchedClimb(null); setKilterInput("");
    setSessionDateTime(getNow());
  };

  // ── GYM FLOW ────────────────────────────────────────────────
  const selectColour = (c) => { setPickedColour(c); setStep(2); };

  const selectHold = (h) => {
    if (h.label==="Other") { setShowCustom(true); setPickedHold(null); return; }
    setPickedHold(h.label); setShowCustom(false); setStep(3);
  };

  const confirmCustomHold = () => {
    if (!customHold.trim()) return;
    setPickedHold(customHold.trim()); setShowCustom(false); setStep(3);
  };

  const selectResult = (sent) => { setPendingSent(sent); setNote(""); setStep(5); };

  // ── KILTER FLOW ─────────────────────────────────────────────
  const handleClimbQRScan = async (url) => {
    setShowScanner(false);
    const climbId = url.match(/climbs\/([a-zA-Z0-9-]+)/)?.[1];
    if (!climbId) { alert("Invalid Kilter climb QR code"); return; }
    await fetchKilterClimb(climbId);
  };

  const handleKilterPaste = async (e) => {
    const text = e.clipboardData.getData("text");
    const climbId = text.match(/climbs\/([a-zA-Z0-9-]+)/)?.[1];
    if (climbId) await fetchKilterClimb(climbId);
  };

  const fetchKilterClimb = async (climbId) => {
    setLoading(true);
    try {
      const res = await fetch(`https://api.kilterboardapp.com/v2/climbs/${climbId}`);
      if (!res.ok) throw new Error("Climb not found");
      const data = await res.json();
      setFetchedClimb({
        id: climbId,
        name: data.name || `Climb ${climbId.slice(0, 8)}`,
        grade: data.difficulty_average || data.difficulty || "V?",
        angle: data.angle || "?",
        setter: data.setter_username || data.setter?.username,
        quality: data.quality_average,
        url: `https://kilterboardapp.com/climbs/${climbId}`,
      });
    } catch (err) {
      alert("Couldn't fetch climb. Try entering URL manually.");
    } finally {
      setLoading(false);
    }
  };

  const quickLogKilter = (sent) => {
    setPendingSent(sent);
    setPickedColour({ name: fetchedClimb.name, hex: T.blue, grade: fetchedClimb.grade });
    setPickedHold("Mixed");
    setStep(5);
  };

  const handleProfileImport = async (url) => {
    setShowProfileScanner(false);
    const userId = url.match(/users\/(\d+)/)?.[1] || url.match(/kilterboardapp\.com\/@([^\/\?]+)/)?.[1];
    if (!userId) { alert("Invalid Kilter profile QR code"); return; }
    await importKilterHistory(userId);
  };

  const importKilterHistory = async (userId) => {
    try {
      setLoading(true);
      const res = await fetch(`https://api.kilterboardapp.com/v2/users/${userId}/ascents?limit=500`);
      if (!res.ok) throw new Error("Failed to fetch.");
      const data = await res.json();
      const ascentsByDate = {};
      data.ascents.forEach(a => {
        const date = a.climbed_at?.slice(0, 10) || new Date().toISOString().slice(0, 10);
        if (!ascentsByDate[date]) ascentsByDate[date] = [];
        ascentsByDate[date].push({
          id: a.id, type: "kilter", climbId: a.climb_uuid,
          climbName: a.climb?.name || `Climb ${a.climb_uuid?.slice(0, 8)}`,
          grade: a.climb?.difficulty_average || a.climb?.difficulty || "V?",
          angle: a.angle || "?", sent: true, note: a.comment || "",
          quality: a.quality, attempts: a.bid_count,
          setter: a.climb?.setter_username,
          kilterUrl: `https://kilterboardapp.com/climbs/${a.climb_uuid}`,
          importedFromKilter: true,
        });
      });
      const importedSessions = Object.entries(ascentsByDate).map(([date, problems]) => ({
        id: `kilter-import-${date}`, gymId: "kilter", gym: "Kilter Board",
        date, problems, importedFromKilter: true,
      }));
      const existingKilterIds = new Set(
        sessions.flatMap(s => s.problems).filter(p => p.type === "kilter").map(p => p.climbId)
      );
      const newSessions = importedSessions.map(s => ({
        ...s, problems: s.problems.filter(p => !existingKilterIds.has(p.climbId))
      })).filter(s => s.problems.length > 0);
      setSessions(prev => [...prev, ...newSessions]);
      setKilterImported(true);
      setKilterUserId(userId);
      setKilterHistoryCount(data.ascents.length);
      save("kilterImported", true);
      save("kilterUserId", userId);
      save("kilterHistoryCount", data.ascents.length);
      alert(`Imported ${data.ascents.length} Kilter sends`);
    } catch (err) {
      alert("Import failed. Make sure your Kilter profile is public.");
    } finally {
      setLoading(false);
    }
  };

  const reimportHistory = () => { if (kilterUserId) importKilterHistory(kilterUserId); };

  // ── COMMIT & SAVE ───────────────────────────────────────────
  const commitProblem = async () => {
    let videoId = null;
    if (videoFile) {
      videoId = `video-${Date.now()}`;
      await saveVideo(videoId, videoFile);
    }
    if (isKilterMode && fetchedClimb) {
      setStaging(s => [...s, {
        id: Date.now(), type: "kilter", climbId: fetchedClimb.id,
        climbName: fetchedClimb.name, grade: fetchedClimb.grade,
        angle: fetchedClimb.angle, setter: fetchedClimb.setter,
        sent: pendingSent, note: note.trim(), quality: fetchedClimb.quality,
        kilterUrl: fetchedClimb.url, videoId,
      }]);
    } else {
      if (!pickedColour || !pickedHold || pendingSent===null) return;
      setStaging(s => [...s, {
        id: Date.now(), colour: pickedColour.name, hex: pickedColour.hex,
        grade: pickedColour.grade, holdType: pickedHold,
        sent: pendingSent, note: note.trim(), videoId,
      }]);
    }
    resetFlow();
  };

  const undoLast = () => setStaging(s => s.slice(0,-1));

  const saveSession = () => {
    if (!staging.length) return;
    const gymName = isKilterMode ? "Kilter Board" : (activeGym?.name || "Unknown Gym");
    const dateTime = new Date(sessionDateTime);
    setSessions(prev => [...prev, {
      id: Date.now(), gymId: activeGymId, gym: gymName,
      date: dateTime.toISOString().slice(0, 10),
      time: dateTime.toTimeString().slice(0, 5),
      problems: staging,
    }]);
    setStaging([]); setShowConfirm(false); resetFlow(); setScreen("history");
  };

  // ── EDIT/DELETE FUNCTIONS ───────────────────────────────────
  const handleEditProblem = (sessionId, problem) => {
    setEditingSessionId(sessionId);
    setEditingProblem(problem);
  };

  const handleSaveEditedProblem = (updatedProblem) => {
    setSessions(prev => prev.map(s => {
      if (s.id !== editingSessionId) return s;
      return {
        ...s,
        problems: s.problems.map(p => p.id === updatedProblem.id ? updatedProblem : p)
      };
    }));
    setEditingProblem(null);
    setEditingSessionId(null);
  };

  const handleDeleteProblem = async (problemId) => {
    const session = sessions.find(s => s.id === editingSessionId);
    const problem = session?.problems.find(p => p.id === problemId);
    if (problem?.videoId) await deleteVideo(problem.videoId);
    
    setSessions(prev => prev.map(s => {
      if (s.id !== editingSessionId) return s;
      const filteredProblems = s.problems.filter(p => p.id !== problemId);
      return { ...s, problems: filteredProblems };
    }).filter(s => s.problems.length > 0));
    
    setEditingProblem(null);
    setEditingSessionId(null);
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm("Delete entire session?")) return;
    const session = sessions.find(s => s.id === sessionId);
    for (const p of session.problems) {
      if (p.videoId) await deleteVideo(p.videoId);
    }
    setSessions(prev => prev.filter(s => s.id !== sessionId));
    setExpanded(null);
  };

  const handleAddVideo = async (sessionId, problemId, file) => {
    if (!file) return;
    const videoId = `video-${Date.now()}`;
    await saveVideo(videoId, file);
    setSessions(prev => prev.map(s => {
      if (s.id !== sessionId) return s;
      return { ...s, problems: s.problems.map(p => p.id === problemId ? { ...p, videoId } : p) };
    }));
  };

  const handleEditVideo = async (sessionId, problemId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const session = sessions.find(s => s.id === sessionId);
      const problem = session?.problems.find(p => p.id === problemId);
      if (problem?.videoId) await deleteVideo(problem.videoId);
      const videoId = `video-${Date.now()}`;
      await saveVideo(videoId, file);
      setSessions(prev => prev.map(s => {
        if (s.id !== sessionId) return s;
        return { ...s, problems: s.problems.map(p => p.id === problemId ? { ...p, videoId } : p) };
      }));
    };
    input.click();
  };

  // ── STATS ───────────────────────────────────────────────────
  const stats = useMemo(() => {
    const gymProblems = sessions.flatMap(s=>s.problems.filter(p=>!p.type || p.type !== "kilter"));
    const kilterProblems = sessions.flatMap(s=>s.problems.filter(p=>p.type==="kilter"));
    const all = [...gymProblems, ...kilterProblems];
    const sent = all.filter(p=>p.sent);
    const best = sent.reduce((b,p)=>GRADE_NUM[p.grade]>GRADE_NUM[b]?p.grade:b,"VB");
    const bestHex = sent.find(p=>p.grade===best)?.hex||T.accent;
    const gradeCounts={};
    sent.forEach(p=>{ if(!gradeCounts[p.grade]) gradeCounts[p.grade]={count:0,hex:p.hex||T.blue}; gradeCounts[p.grade].count++; });
    const holdCounts={}, holdSent={};
    gymProblems.forEach(p=>{ holdCounts[p.holdType]=(holdCounts[p.holdType]||0)+1; });
    gymProblems.filter(p=>p.sent).forEach(p=>{ holdSent[p.holdType]=(holdSent[p.holdType]||0)+1; });
    return { 
      total:all.length, sent:sent.length, sessions:sessions.length, best, bestHex, gradeCounts, holdCounts, holdSent,
      rate:all.length?Math.round(sent.length/all.length*100):0,
      gymSends: gymProblems.filter(p=>p.sent).length,
      kilterSends: kilterProblems.length,
    };
  }, [sessions]);

  const progressData = useMemo(() => sessions.map(s=>{
    const sent=s.problems.filter(p=>p.sent);
    const best=sent.reduce((b,p)=>GRADE_NUM[p.grade]>GRADE_NUM[b]?p.grade:b,"VB");
    return { date:s.date.slice(5), grade:GRADE_NUM[best], sends:sent.length };
  }), [sessions]);

  const holdChartData = useMemo(() => Object.keys(stats.holdCounts).map(h=>({
    hold:h.length>9?h.slice(0,9)+"...":h, tried:stats.holdCounts[h], sent:stats.holdSent[h]||0,
  })).sort((a,b)=>b.tried-a.tried), [stats]);

  const exportCSV = () => {
    const rows=[["Date","Time","Source","Gym/Board","Colour/Climb","Grade","Hold Type","Sent","Notes"]];
    sessions.forEach(s=>s.problems.forEach(p=>{
      if(p.type==="kilter") {
        rows.push([s.date,s.time||"","Kilter",`Kilter Board (${p.angle})`,p.climbName,p.grade,"Mixed","Yes",p.note||""]);
      } else {
        rows.push([s.date,s.time||"","Gym",s.gym,p.colour,p.grade,p.holdType,p.sent?"Yes":"No",p.note||""]);
      }
    }));
    const csv=rows.map(r=>r.map(c=>`"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
    const a=document.createElement("a"); a.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"}));
    a.download=`bloc-tracker-${new Date().toISOString().slice(0,10)}.csv`; a.click();
  };

  // ── STYLES ──────────────────────────────────────────────────
  const inp  = { background:T.surface, border:`1px solid ${T.border}`, color:T.text, borderRadius:10, padding:"13px 16px", fontSize:16, outline:"none", width:"100%", boxSizing:"border-box" };
  const lbl  = { color:T.muted, fontSize:11, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:8, display:"block" };
  const card = { background:T.card, border:`1px solid ${T.border}`, borderRadius:14, padding:16, marginBottom:14 };
  const navBtn = (active) => ({ flex:1, padding:"12px 4px 10px", background:"none", border:"none", color:active?T.accent:T.muted, fontSize:10, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:3 });
  const STEPS = isKilterMode ? ["Scan","Video","Result","Notes"] : ["Colour","Hold","Video","Result","Notes"];

  return (
    <div style={{ minHeight:"100dvh", background:T.bg, color:T.text, fontFamily:"'DM Sans',system-ui,sans-serif", paddingBottom:80 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;600;700;800&display=swap');
        * { box-sizing:border-box; -webkit-tap-highlight-color:transparent; }
        button:active { opacity:0.82; transform:scale(0.97); }
        input:focus,textarea:focus,select:focus { border-color:${T.accent} !important; outline:none; }
        textarea { resize:none; font-family:inherit; }
      `}</style>

      {/* HEADER */}
      <div style={{ background:T.bg, borderBottom:`1px solid ${T.border}`, padding:"14px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:10 }}>
        <div>
          <div style={{ fontFamily:"'Bebas Neue',Impact,sans-serif", fontSize:26, letterSpacing:"0.06em", color:T.accent, lineHeight:1 }}>{APP_NAME}</div>
          <div style={{ fontSize:10, color:T.muted, letterSpacing:"0.15em", textTransform:"uppercase" }}>{APP_TAGLINE}</div>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          {screen==="log" && staging.length>0 && (
            <button onClick={()=>setShowConfirm(true)} style={{ background:T.accent, color:"#000", border:"none", borderRadius:10, padding:"10px 16px", fontWeight:800, fontSize:13, cursor:"pointer" }}>
              Save ({staging.length})
            </button>
          )}
          {screen==="history" && (
            <button onClick={exportCSV} style={{ background:"none", color:T.accent, border:`1px solid ${T.accent}44`, borderRadius:10, padding:"8px 14px", fontWeight:700, fontSize:12, cursor:"pointer" }}>Export CSV</button>
          )}
        </div>
      </div>

      {/* ━━━ LOG SCREEN ━━━ */}
      {screen==="log" && (
        <div style={{ padding:"20px 16px" }}>

          {/* Date & Time */}
          <div style={{ marginBottom:20 }}>
            <label style={lbl}>Date & Time</label>
            <input
              type="datetime-local"
              value={sessionDateTime}
              onChange={e => setSessionDateTime(e.target.value)}
              style={inp}
            />
          </div>

          {/* Gym selector */}
          <div style={{ marginBottom:20 }}>
            <label style={lbl}>Location</label>
            <select value={activeGymId} onChange={e=>{ setActiveGymId(e.target.value); resetFlow(); }} style={inp}>
              {gyms.map(g=><option key={g.id} value={g.id}>{g.name}</option>)}
              <option value="kilter">Kilter Board</option>
            </select>
          </div>

          {/* Step indicator */}
          <div style={{ display:"flex", gap:6, marginBottom:22 }}>
            {STEPS.map((s,i)=>{
              const stepNum = i + 1;
              const done = step > stepNum;
              const active = step === stepNum;
              return (
                <div key={s} style={{ flex:1, textAlign:"center" }}>
                  <div style={{ height:3, borderRadius:2, marginBottom:5, background:done?T.green:active?T.accent:T.border }} />
                  <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:done?T.green:active?T.accent:T.muted }}>{s}</div>
                </div>
              );
            })}
          </div>

          {/* ━━━ KILTER STEP 1 — Scan QR ━━━ */}
          {step===1 && isKilterMode && (
            <>
              <label style={lbl}>Step 1 — Scan the climb QR code</label>
              {!showScanner && !fetchedClimb && (
                <button onClick={() => setShowScanner(true)} style={{
                  background: `linear-gradient(135deg, ${T.blue} 0%, #3b82f6 100%)`,
                  color: "#fff", border: "none", borderRadius: 14, padding: "24px 20px",
                  fontWeight: 800, fontSize: 18, cursor: "pointer", width: "100%",
                  boxShadow: "0 4px 20px rgba(96, 165, 250, 0.3)",
                }}>
                  SCAN KILTER QR CODE
                </button>
              )}
              {showScanner && !fetchedClimb && (
                <div style={{ background: T.card, borderRadius: 14, overflow: "hidden", marginBottom: 16 }}>
                  <QRScanner onScan={handleClimbQRScan} />
                  <button onClick={() => setShowScanner(false)} style={{ background: T.surface, color: T.muted, border: "none", padding: 12, width: "100%", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Cancel</button>
                </div>
              )}
              {!showScanner && !fetchedClimb && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ textAlign: "center", color: T.muted, fontSize: 12, marginBottom: 8 }}>or paste URL manually</div>
                  <input placeholder="https://kilterboardapp.com/climbs/..." value={kilterInput} onChange={e => setKilterInput(e.target.value)} onPaste={handleKilterPaste} style={inp} />
                </div>
              )}
              {loading && <div style={{ textAlign: "center", padding: 40, color: T.muted }}>Fetching climb...</div>}
              {fetchedClimb && (
                <>
                  <div style={{ background: T.card, border: `2px solid ${T.blue}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
                    <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 6 }}>{fetchedClimb.name}</div>
                    <div style={{ color: T.muted, fontSize: 14 }}>
                      <span style={{ color: T.accent, fontWeight: 800 }}>{fetchedClimb.grade}</span>
                      {" • "}{fetchedClimb.angle}{fetchedClimb.setter && ` • by ${fetchedClimb.setter}`}
                    </div>
                  </div>
                  <button onClick={() => setStep(2)} style={{ background: T.accent, color: "#000", border: "none", borderRadius: 12, padding: 16, fontWeight: 900, fontSize: 16, cursor: "pointer", width: "100%" }}>Next</button>
                  <button onClick={() => { setFetchedClimb(null); setKilterInput(""); }} style={{ background: "none", border: `1px solid ${T.border}`, color: T.muted, borderRadius: 10, padding: "10px", fontWeight: 700, fontSize: 13, cursor: "pointer", width: "100%", marginTop: 12 }}>Scan Different Climb</button>
                </>
              )}
            </>
          )}

          {/* ━━━ GYM STEP 1 — Pick colour ━━━ */}
          {step===1 && !isKilterMode && (
            <>
              <label style={lbl}>Step 1 — Pick the route colour</label>
              {gymColours.length===0 ? (
                <div style={{ textAlign:"center", color:T.muted, padding:"40px 0" }}>
                  No colours set for this gym.
                  <button onClick={()=>setScreen("gyms")} style={{ background:"none", color:T.accent, border:"none", fontWeight:700, fontSize:14, cursor:"pointer", marginTop:8 }}>Set them up in Settings</button>
                </div>
              ) : (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
                  {gymColours.map((c,i)=>(
                    <button key={i} onClick={()=>selectColour(c)} style={{ background:"none", border:"none", padding:0, cursor:"pointer" }}>
                      <ColourPill colour={c.name} hex={c.hex} grade={c.grade} selected={pickedColour?.name===c.name} />
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ━━━ GYM STEP 2 — Hold type ━━━ */}
          {step===2 && !isKilterMode && (
            <>
              <Crumbs colour={pickedColour} onBackColour={resetFlow} />
              <label style={lbl}>Step 2 — What type of hold?</label>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:14 }}>
                {HOLD_TYPES.map((h,i)=>(
                  <button key={i} onClick={()=>selectHold(h)} style={{
                    background:pickedHold===h.label?T.accent+"33":T.card,
                    border:pickedHold===h.label?`2px solid ${T.accent}`:`1px solid ${T.border}`,
                    color:pickedHold===h.label?T.accent:T.text,
                    borderRadius:12, padding:"14px 8px", fontWeight:700, fontSize:13, cursor:"pointer",
                  }}>
                    {h.label}
                  </button>
                ))}
              </div>
              {showCustom && (
                <div style={{ display:"flex", gap:10 }}>
                  <input autoFocus placeholder="Describe the hold type..." value={customHold} onChange={e=>setCustomHold(e.target.value)} onKeyDown={e=>e.key==="Enter"&&confirmCustomHold()} style={inp} />
                  <button onClick={confirmCustomHold} style={{ background:T.accent, color:"#000", border:"none", borderRadius:10, padding:"0 18px", fontWeight:800, fontSize:15, cursor:"pointer" }}>Next</button>
                </div>
              )}
            </>
          )}

          {/* ━━━ STEP 2 (Kilter) / STEP 3 (Gym) — Record video ━━━ */}
          {((step===2 && isKilterMode) || (step===3 && !isKilterMode)) && (
            <>
              <Crumbs colour={pickedColour} hold={pickedHold} onBackHold={() => setStep(isKilterMode ? 1 : 2)} />
              <label style={lbl}>{isKilterMode ? "Step 2" : "Step 3"} — Record your attempt <span style={{ color: T.border, fontWeight: 400 }}>(optional)</span></label>
              {videoFile && (
                <div style={{ marginBottom: 12, borderRadius: 12, overflow: "hidden" }}>
                  <video src={URL.createObjectURL(videoFile)} controls style={{ width: "100%", maxHeight: 300 }} />
                </div>
              )}
              {!videoFile && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                  <label style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "24px 12px", fontWeight: 700, fontSize: 14, cursor: "pointer", textAlign: "center", color: T.text }}>
                    Record
                    <input type="file" accept="video/*" capture="environment" onChange={e => setVideoFile(e.target.files[0])} style={{ display: "none" }} />
                  </label>
                  <label style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "24px 12px", fontWeight: 700, fontSize: 14, cursor: "pointer", textAlign: "center", color: T.text }}>
                    Upload
                    <input type="file" accept="video/*" onChange={e => setVideoFile(e.target.files[0])} style={{ display: "none" }} />
                  </label>
                </div>
              )}
              {videoFile && (
                <button onClick={() => setVideoFile(null)} style={{ background: "none", border: `1px solid ${T.border}`, color: T.muted, borderRadius: 10, padding: "8px 12px", fontWeight: 700, fontSize: 12, cursor: "pointer", width: "100%", marginBottom: 16 }}>Remove Video</button>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <button onClick={() => setStep(isKilterMode ? 3 : 4)} style={{ background: T.surface, color: T.muted, border: `1px solid ${T.border}`, borderRadius: 12, padding: 15, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Skip</button>
                <button onClick={() => setStep(isKilterMode ? 3 : 4)} style={{ background: T.accent, color: "#000", border: "none", borderRadius: 12, padding: 15, fontWeight: 900, fontSize: 15, cursor: "pointer" }}>Next</button>
              </div>
            </>
          )}

          {/* ━━━ STEP 3 (Kilter) / STEP 4 (Gym) — Sent or project ━━━ */}
          {((step===3 && isKilterMode) || (step===4 && !isKilterMode)) && (
            <>
              <Crumbs colour={pickedColour} hold={pickedHold} onBackHold={() => setStep(isKilterMode ? 2 : 3)} />
              <label style={lbl}>{isKilterMode ? "Step 3" : "Step 4"} — Did you send it?</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <button onClick={() => selectResult(true)} style={{ background: "#4ade8022", border: "2px solid #4ade80", color: "#4ade80", borderRadius: 12, padding: "18px 12px", fontWeight: 800, fontSize: 16, cursor: "pointer" }}>SENT</button>
                <button onClick={() => selectResult(false)} style={{ background: "#f8717122", border: "2px solid #f87171", color: "#f87171", borderRadius: 12, padding: "18px 12px", fontWeight: 800, fontSize: 16, cursor: "pointer" }}>PROJECT</button>
              </div>
            </>
          )}

          {/* ━━━ STEP 5 — Notes ━━━ */}
          {step===5 && (
            <>
              <Crumbs colour={pickedColour} hold={pickedHold} onBackHold={() => setStep(isKilterMode ? 3 : 4)} />
              <div style={{ marginBottom: 18 }}>
                <span style={{ background: pendingSent ? "#4ade8022" : "#f8717122", color: pendingSent ? T.green : T.red, border: `2px solid ${pendingSent ? T.green : T.red}`, borderRadius: 10, padding: "8px 18px", fontWeight: 900, fontSize: 16 }}>{pendingSent ? "SENT" : "PROJECT"}</span>
              </div>
              <label style={lbl}>{pendingSent ? "What worked?" : "What held you back?"} <span style={{ color: T.border, fontWeight: 400 }}>(optional)</span></label>
              <textarea autoFocus rows={3} placeholder={pendingSent ? SENT_PROMPT : PROJECT_PROMPT} value={note} onChange={e => setNote(e.target.value)} style={{ ...inp, lineHeight: 1.5, paddingTop: 14 }} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 16 }}>
                <button onClick={() => { setNote(""); commitProblem(); }} style={{ background: T.surface, color: T.muted, border: `1px solid ${T.border}`, borderRadius: 12, padding: 15, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Skip</button>
                <button onClick={commitProblem} style={{ background: T.accent, color: "#000", border: "none", borderRadius: 12, padding: 15, fontWeight: 900, fontSize: 15, cursor: "pointer" }}>Log</button>
              </div>
            </>
          )}

          {/* Staging list */}
          {staging.length>0 && (
            <div style={{ ...card, marginTop:24 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                <span style={{ fontWeight:800, fontSize:14 }}>This session ({staging.length})</span>
                <button onClick={undoLast} style={{ background:"none", border:`1px solid ${T.border}`, color:T.muted, borderRadius:8, padding:"5px 12px", fontSize:12, fontWeight:700, cursor:"pointer" }}>Undo</button>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {[...staging].reverse().map(p=>(
                  <div key={p.id}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      {p.type==="kilter" ? <div style={{ fontSize:11, color:T.blue, fontWeight:700 }}>K</div> : <div style={{ width:11, height:11, borderRadius:"50%", background:p.hex }} />}
                      <span style={{ fontWeight:700, fontSize:14, flex:1 }}>{p.type==="kilter" ? p.climbName : `${p.colour} / ${p.grade}`}</span>
                      {p.type==="kilter" && <span style={{ color:T.muted, fontSize:11 }}>{p.angle}</span>}
                      {!p.type && <span style={{ color:T.muted, fontSize:12 }}>{p.holdType}</span>}
                      <span style={{ fontWeight:800, fontSize:13, color:p.sent?T.green:T.red }}>{p.sent?"SENT":"PROJECT"}</span>
                    </div>
                    {p.note && <div style={{ marginLeft:19, marginTop:3, color:T.muted, fontSize:12, fontStyle:"italic" }}>"{p.note}"</div>}
                    {p.videoId && <div style={{ marginLeft:19, marginTop:3, color:T.blue, fontSize:11, fontWeight:700 }}>Video attached</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ━━━ HISTORY SCREEN ━━━ */}
      {screen==="history" && (
        <div style={{ padding:"20px 16px" }}>
          {sessions.length===0 && <div style={{ textAlign:"center", color:T.muted, padding:"60px 0", fontSize:15 }}>No sessions yet. Hit Log to record your first climb.</div>}
          {[...sessions].reverse().map(s=>{
            const sent = s.problems.filter(p=>p.sent);
            const bestP = sent.reduce((b,p)=>GRADE_NUM[p.grade||"VB"]>GRADE_NUM[b?.grade||"VB"]?p:b, null);
            const isOpen = expanded===s.id;
            return (
              <div key={s.id} style={{ ...card, padding:0, overflow:"hidden" }}>
                <div onClick={()=>setExpanded(isOpen?null:s.id)} style={{ padding:"14px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", cursor:"pointer" }}>
                  <div>
                    <div style={{ fontWeight:800, fontSize:16 }}>
                      {s.gym}
                      {s.importedFromKilter && <span style={{ marginLeft: 8, background: T.blue + "22", color: T.blue, fontSize: 10, padding: "2px 8px", borderRadius: 6, fontWeight: 700 }}>IMPORTED</span>}
                    </div>
                    <div style={{ color:T.muted, fontSize:13, marginTop:2 }}>{s.date} {s.time && `at ${s.time}`} • {s.problems.length} problems</div>
                  </div>
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    {bestP && <span style={{ background:bestP.hex||T.blue, color:isLight(bestP.hex||T.blue)?"#111":"#fff", borderRadius:6, padding:"3px 10px", fontSize:11, fontWeight:800 }}>{bestP.type==="kilter" ? bestP.grade : `${bestP.colour} / ${bestP.grade}`}</span>}
                    <span style={{ color:T.muted, fontSize:18 }}>{isOpen?"▲":"▼"}</span>
                  </div>
                </div>
                {isOpen && (
                  <div style={{ borderTop:`1px solid ${T.border}`, padding:"12px 16px" }}>
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:12 }}>
                      <span style={{ background:T.green+"22", color:T.green, border:`1px solid ${T.green}44`, borderRadius:6, padding:"3px 10px", fontSize:12, fontWeight:700 }}>{sent.length} sends</span>
                      <span style={{ background:T.muted+"22", color:T.muted, border:`1px solid ${T.muted}44`, borderRadius:6, padding:"3px 10px", fontSize:12, fontWeight:700 }}>{s.problems.length-sent.length} projects</span>
                      <button onClick={() => handleDeleteSession(s.id)} style={{ marginLeft:"auto", background:"none", color:T.red, border:`1px solid ${T.red}44`, borderRadius:6, padding:"3px 10px", fontSize:12, fontWeight:700, cursor:"pointer" }}>Delete Session</button>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                      {s.problems.map((p,i)=>(
                        <div key={i} style={{ background:T.surface, borderRadius:10, padding:12 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                            {p.type==="kilter" ? <div style={{ fontSize:11, color:T.blue, fontWeight:700, width:11 }}>K</div> : <div style={{ width:11, height:11, borderRadius:"50%", background:p.hex }} />}
                            <span style={{ fontWeight:700, fontSize:14, flex:1 }}>{p.type==="kilter" ? p.climbName : `${p.colour} / ${p.grade}`}</span>
                            {p.type==="kilter" && <span style={{ color:T.muted, fontSize:12 }}>{p.angle}</span>}
                            {!p.type && <span style={{ color:T.muted, fontSize:12 }}>{p.holdType}</span>}
                            <span style={{ fontWeight:800, fontSize:13, color:p.sent?T.green:T.red }}>{p.sent?"SENT":"PROJECT"}</span>
                          </div>
                          {p.note && <div style={{ color:T.muted, fontSize:12, fontStyle:"italic", marginBottom:6 }}>"{p.note}"</div>}
                          
                          {/* Video */}
                          {p.videoId && (
                            <div style={{ marginBottom:8 }}>
                              <VideoPlayer videoId={p.videoId} style={{ width: "100%", maxHeight: 200, borderRadius: 8 }} />
                              <button onClick={() => handleEditVideo(s.id, p.id)} style={{ marginTop: 6, background: "none", border: `1px solid ${T.border}`, color: T.muted, borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Replace Video</button>
                            </div>
                          )}
                          {!p.videoId && (
                            <label style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer", color: T.accent, display: "inline-block", marginBottom:8 }}>
                              + Add Video
                              <input type="file" accept="video/*" onChange={(e) => handleAddVideo(s.id, p.id, e.target.files[0])} style={{ display: "none" }} />
                            </label>
                          )}

                          {/* Action buttons */}
                          <div style={{ display:"flex", gap:8, marginTop:8 }}>
                            <button onClick={() => handleEditProblem(s.id, p)} style={{ background:T.card, border:`1px solid ${T.border}`, color:T.text, borderRadius:8, padding:"6px 12px", fontSize:11, fontWeight:700, cursor:"pointer" }}>Edit</button>
                            <ShareButton problem={p} videoId={p.videoId} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ━━━ STATS SCREEN ━━━ */}
      {screen==="stats" && (
        <div style={{ padding:"20px 16px" }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:20 }}>
            <div style={{ ...card, gridColumn:"1 / -1" }}>
              <div style={{ color:T.muted, fontSize:10, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:6 }}>Total Sends</div>
              <div style={{ display:"flex", alignItems:"baseline", gap:12 }}>
                <div style={{ color:T.accent, fontSize:36, fontWeight:900, lineHeight:1, fontFamily:"'Bebas Neue',Impact,sans-serif" }}>{stats.sent}</div>
                <div style={{ color:T.muted, fontSize:13 }}>{stats.gymSends} gym • {stats.kilterSends} Kilter</div>
              </div>
            </div>
            {[
              { label:"Sessions", value:stats.sessions },
              { label:"Send Rate", value:`${stats.rate}%`, color:T.blue },
              { label:"Best Grade", value:stats.best, color:stats.bestHex },
            ].map(c=>(
              <div key={c.label} style={card}>
                <div style={{ color:T.muted, fontSize:10, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:6 }}>{c.label}</div>
                <div style={{ color:c.color||T.accent, fontSize:36, fontWeight:900, lineHeight:1, fontFamily:"'Bebas Neue',Impact,sans-serif" }}>{c.value}</div>
              </div>
            ))}
          </div>
          {holdChartData.length>0 && (
            <div style={card}>
              <div style={{ fontWeight:800, fontSize:13, letterSpacing:"0.08em", textTransform:"uppercase", color:T.muted, marginBottom:4 }}>Hold Types — Tried vs Sent</div>
              <div style={{ fontSize:11, color:T.muted, marginBottom:14 }}>Spot your weaknesses</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={holdChartData} layout="vertical" margin={{left:0,right:16}}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} horizontal={false} />
                  <XAxis type="number" stroke={T.muted} tick={{fill:T.muted,fontSize:10}} />
                  <YAxis type="category" dataKey="hold" stroke={T.muted} tick={{fill:T.muted,fontSize:11}} width={76} />
                  <Tooltip contentStyle={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,color:T.text}} />
                  <Bar dataKey="tried" fill={T.muted} radius={[0,4,4,0]} name="Tried" />
                  <Bar dataKey="sent" fill={T.green} radius={[0,4,4,0]} name="Sent" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {Object.keys(stats.gradeCounts).length>0 && (
            <div style={card}>
              <div style={{ fontWeight:800, fontSize:13, letterSpacing:"0.08em", textTransform:"uppercase", color:T.muted, marginBottom:14 }}>Sends by Grade</div>
              {GRADES.filter(g=>stats.gradeCounts[g]).map(g=>{
                const {count,hex}=stats.gradeCounts[g];
                const max=Math.max(...Object.values(stats.gradeCounts).map(v=>v.count),1);
                return (
                  <div key={g} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                    <div style={{ width:32, color:T.muted, fontSize:12, fontWeight:700 }}>{g}</div>
                    <div style={{ flex:1, height:12, background:T.border, borderRadius:6, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${(count/max)*100}%`, background:hex, borderRadius:6 }} />
                    </div>
                    <div style={{ width:18, color:T.text, fontSize:13, fontWeight:700, textAlign:"right" }}>{count}</div>
                  </div>
                );
              })}
            </div>
          )}
          {progressData.length>=2 && (
            <div style={card}>
              <div style={{ fontWeight:800, fontSize:13, letterSpacing:"0.08em", textTransform:"uppercase", color:T.muted, marginBottom:14 }}>Grade Progress</div>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={progressData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                  <XAxis dataKey="date" stroke={T.muted} tick={{fill:T.muted,fontSize:10}} />
                  <YAxis domain={[0,13]} tickFormatter={i=>GRADES[i]||""} stroke={T.muted} tick={{fill:T.muted,fontSize:10}} />
                  <Tooltip contentStyle={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,color:T.text}} formatter={v=>[GRADES[v],"Grade"]} />
                  <Line type="monotone" dataKey="grade" stroke={T.accent} strokeWidth={2.5} dot={{fill:T.accent,r:4}} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          {sessions.length<2 && <div style={{ textAlign:"center", color:T.muted, fontSize:13 }}>Log a few sessions to see your progress chart.</div>}
        </div>
      )}

      {/* ━━━ SETTINGS SCREEN ━━━ */}
      {screen==="gyms" && (
        <div style={{ padding:"20px 16px" }}>
          <div style={{ ...card, borderLeft: `4px solid ${T.blue}` }}>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8 }}>Import Kilter History</div>
            <div style={{ color: T.muted, fontSize: 13, marginBottom: 12 }}>One-time import of all your past Kilter sends</div>
            {!kilterImported ? (
              <>
                <button onClick={() => setShowProfileScanner(true)} style={{ background: T.blue, color: "#fff", border: "none", borderRadius: 12, padding: "16px 20px", fontWeight: 800, fontSize: 15, cursor: "pointer", width: "100%" }}>Scan Your Kilter Profile QR</button>
                {showProfileScanner && (
                  <div style={{ background: T.surface, borderRadius: 12, overflow: "hidden", marginTop: 12 }}>
                    <QRScanner onScan={handleProfileImport} />
                    <button onClick={() => setShowProfileScanner(false)} style={{ background: T.border, color: T.text, border: "none", padding: 12, width: "100%", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Cancel</button>
                  </div>
                )}
                <div style={{ marginTop: 12, padding: 12, background: T.surface, borderRadius: 10, fontSize: 12, color: T.muted }}>
                  How to find your profile QR:<br/>1. Open Kilter app<br/>2. Go to Profile tab<br/>3. Tap Share or username<br/>4. Show the QR code to this camera
                </div>
              </>
            ) : (
              <div style={{ background: T.green + "22", border: `1px solid ${T.green}44`, borderRadius: 10, padding: 16 }}>
                <div style={{ fontWeight: 800 }}>History Imported</div>
                <div style={{ color: T.muted, fontSize: 12 }}>{kilterHistoryCount} sends from Kilter</div>
                <button onClick={reimportHistory} style={{ marginTop:8, background: "none", border: `1px solid ${T.border}`, color: T.accent, borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Re-import (update history)</button>
              </div>
            )}
          </div>
          {gyms.map(gym=>(
            <GymEditor key={gym.id} gym={gym} onSave={updated=>setGyms(gs=>gs.map(g=>g.id===gym.id?updated:g))} onDelete={()=>setGyms(gs=>gs.filter(g=>g.id!==gym.id))} />
          ))}
          <button onClick={()=>setGyms(gs=>[...gs,{id:"g"+Date.now(),name:"New Gym",colours:[]}])} style={{ background:T.card, color:T.accent, border:`1px solid ${T.accent}44`, borderRadius:14, padding:16, fontWeight:800, fontSize:15, cursor:"pointer", width:"100%" }}>Add Gym</button>
        </div>
      )}

      {/* ━━━ SAVE CONFIRM ━━━ */}
      {showConfirm && (
        <div style={{ position:"fixed", inset:0, background:"#000c", display:"flex", alignItems:"flex-end", zIndex:50, padding:16 }}>
          <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:20, padding:24, width:"100%" }}>
            <div style={{ fontFamily:"'Bebas Neue',Impact,sans-serif", fontSize:26, letterSpacing:"0.05em", marginBottom:6 }}>Save Session?</div>
            <div style={{ color:T.muted, fontSize:14, marginBottom:8 }}>{isKilterMode ? "Kilter Board" : activeGym?.name} • {new Date(sessionDateTime).toLocaleDateString()}</div>
            <div style={{ display:"flex", gap:16, marginBottom:20, fontSize:15 }}>
              <span style={{ color:T.green, fontWeight:800 }}>{staging.filter(p=>p.sent).length} sends</span>
              <span style={{ color:T.red, fontWeight:800 }}>{staging.filter(p=>!p.sent).length} projects</span>
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={()=>setShowConfirm(false)} style={{ flex:1, background:T.border, color:T.text, border:"none", borderRadius:12, padding:15, fontWeight:800, fontSize:15, cursor:"pointer" }}>Cancel</button>
              <button onClick={saveSession} style={{ flex:2, background:T.accent, color:"#000", border:"none", borderRadius:12, padding:15, fontWeight:900, fontSize:15, cursor:"pointer" }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* ━━━ EDIT MODAL ━━━ */}
      {editingProblem && (
        <EditProblemModal
          problem={editingProblem}
          onSave={handleSaveEditedProblem}
          onClose={() => { setEditingProblem(null); setEditingSessionId(null); }}
          onDelete={handleDeleteProblem}
        />
      )}

      {/* ━━━ BOTTOM NAV ━━━ */}
      <nav style={{ position:"fixed", bottom:0, left:0, right:0, background:T.surface, borderTop:`1px solid ${T.border}`, display:"flex", zIndex:20 }}>
        {[{id:"log",label:"Log"},{id:"history",label:"History"},{id:"stats",label:"Stats"},{id:"gyms",label:"Settings"}].map(t=>(
          <button key={t.id} onClick={()=>{ setScreen(t.id); if(t.id==="log") resetFlow(); }} style={navBtn(screen===t.id)}>{t.label}</button>
        ))}
      </nav>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// GYM EDITOR COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const ALL_SWATCHES = Object.entries(COLOUR_HEX).map(([n,h])=>({name:n.charAt(0).toUpperCase()+n.slice(1),hex:h}));

function GymEditor({ gym, onSave, onDelete }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(gym.name);
  const [colours, setColours] = useState(gym.colours);
  const [picked, setPicked] = useState(ALL_SWATCHES[0]);
  const [pickedGrade, setPickedGrade] = useState("V4");

  const i2 = { background:"#0d0f14", border:"1px solid #252d40", color:"#f0f4ff", borderRadius:10, padding:"12px 14px", fontSize:15, outline:"none", width:"100%", boxSizing:"border-box" };
  const l2 = { color:"#8892a4", fontSize:11, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:6, display:"block" };

  const doSave = () => { onSave({...gym,name,colours}); setOpen(false); };

  return (
    <div style={{ background:"#1c2130", border:"1px solid #252d40", borderRadius:14, marginBottom:14, overflow:"hidden" }}>
      <div onClick={()=>setOpen(o=>!o)} style={{ padding:16, display:"flex", justifyContent:"space-between", alignItems:"center", cursor:"pointer" }}>
        <div>
          <div style={{ fontWeight:800, fontSize:17 }}>{gym.name}</div>
          <div style={{ color:"#8892a4", fontSize:13, marginTop:2 }}>{gym.colours.length} colours mapped</div>
        </div>
        <span style={{ color:"#8892a4", fontSize:20 }}>{open?"▲":"▼"}</span>
      </div>
      {open && (
        <div style={{ borderTop:"1px solid #252d40", padding:16 }}>
          <div style={{ marginBottom:16 }}>
            <label style={l2}>Gym Name</label>
            <input value={name} onChange={e=>setName(e.target.value)} style={i2} />
          </div>
          <label style={l2}>Colour to Grade</label>
          {colours.map((c,i)=>(
            <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid #252d40" }}>
              <div style={{ width:16, height:16, borderRadius:"50%", background:c.hex }} />
              <span style={{ flex:1, fontWeight:700, fontSize:14 }}>{c.name}</span>
              <span style={{ color:"#8892a4" }}>to</span>
              <span style={{ color:"#e8ff47", fontWeight:800 }}>{c.grade}</span>
              <button onClick={()=>setColours(cs=>cs.filter((_,j)=>j!==i))} style={{ background:"none", border:"none", color:"#8892a4", fontSize:18, cursor:"pointer" }}>×</button>
            </div>
          ))}
          {colours.length===0 && <div style={{ color:"#8892a4", fontSize:13, marginBottom:8 }}>No colours yet</div>}
          <div style={{ background:"#0d0f14", borderRadius:10, padding:14, marginTop:14, marginBottom:16 }}>
            <label style={l2}>Add Colour</label>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:12 }}>
              {ALL_SWATCHES.map(sw=>(
                <button key={sw.name} onClick={()=>setPicked(sw)} style={{ background:sw.hex, color:isLight(sw.hex)?"#111":"#fff", border:picked.name===sw.name?"3px solid #fff":"2px solid transparent", borderRadius:8, padding:"6px 12px", fontWeight:800, fontSize:12, cursor:"pointer" }}>{sw.name}</button>
              ))}
            </div>
            <div style={{ display:"flex", gap:10, alignItems:"flex-end" }}>
              <div style={{ flex:1 }}>
                <label style={l2}>V-Grade</label>
                <select value={pickedGrade} onChange={e=>setPickedGrade(e.target.value)} style={i2}>
                  {GRADES.map(g=><option key={g}>{g}</option>)}
                </select>
              </div>
              <button onClick={()=>setColours(cs=>[...cs,{name:picked.name,hex:picked.hex,grade:pickedGrade}])} style={{ background:"#252d40", color:"#f0f4ff", border:"none", borderRadius:10, padding:"12px 18px", fontWeight:800, fontSize:14, cursor:"pointer" }}>Add</button>
            </div>
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={onDelete} style={{ background:"none", color:"#f87171", border:"1px solid #f8717144", borderRadius:10, padding:12, fontWeight:700, fontSize:14, cursor:"pointer" }}>Delete</button>
            <button onClick={doSave} style={{ flex:1, background:"#e8ff47", color:"#000", border:"none", borderRadius:10, padding:14, fontWeight:900, fontSize:15, cursor:"pointer" }}>Save Gym</button>
          </div>
        </div>
      )}
    </div>
  );
}